import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import './index.css';

const VillaBookingCalendar = ({ bookings, setBookings, onBookNow }) => {
  const [villas, setVillas] = useState([]);
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVillas = async () => {
      try {
        const villasCollection = collection(db, 'villas');
        const villaSnapshot = await getDocs(villasCollection);
        const villaList = villaSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVillas(villaList);
        if (villaList.length > 0) {
          setSelectedVilla(villaList[0].id);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching villas:', err);
        setError('Failed to fetch villas');
        setLoading(false);
      }
    };
    fetchVillas();
  }, []);

  useEffect(() => {
    const villa = villas.find((v) => v.id === selectedVilla);
    if (villa) {
      const today = new Date();
      const todayStr = formatDateToUTC(today);
      const filtered = (villa.blockedDates || []).filter((date) => {
        return /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= todayStr;
      });
      setBlockedDates(filtered);
      setSelectedDates([]);
    }
  }, [selectedVilla, villas]);

  useEffect(() => {
    const updateBlockedDates = async () => {
      if (bookings.length > 0 && selectedVilla) {
        const newBlockedDates = [];
        const today = new Date();
        const todayStr = formatDateToUTC(today);

        bookings
          .filter((booking) => booking.villaId === selectedVilla && booking.status === 'active')
          .forEach((booking) => {
            let currentDate = new Date(booking.startDate);
            const endDate = new Date(booking.endDate);
            while (currentDate <= endDate) {
              const formatted = formatDateToUTC(currentDate);
              if (formatted >= todayStr) {
                newBlockedDates.push(formatted);
              }
              currentDate.setDate(currentDate.getDate() + 1);
            }
          });

        try {
          const villaRef = doc(db, 'villas', selectedVilla);
          await updateDoc(villaRef, {
            blockedDates: arrayUnion(...newBlockedDates),
          });
          setBlockedDates((prev) => [...new Set([...prev, ...newBlockedDates])]);
        } catch (err) {
          console.error('Error updating blocked dates:', err);
          setError('Failed to update blocked dates from bookings');
        }
      }
    };
    updateBlockedDates();
  }, [bookings, selectedVilla]);

  const handleVillaChange = (e) => {
    setSelectedVilla(e.target.value);
  };

  const formatDateToUTC = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      .toISOString()
      .split('T')[0];
  };

  const isDateBlocked = (date) => {
    const formattedDate = formatDateToUTC(date);
    return formattedDate ? blockedDates.includes(formattedDate) : false;
  };

  const isDateSelected = (date) => {
    if (selectedDates.length === 0) return false;
    if (selectedDates.length === 1) {
      return date.toDateString() === selectedDates[0].toDateString();
    }
    return date >= selectedDates[0] && date <= selectedDates[1];
  };

  const isInRange = (date) => {
    if (selectedDates.length !== 2) return false;
    return date > selectedDates[0] && date < selectedDates[1];
  };

  const handleDateClick = async (date) => {
    const formattedDate = formatDateToUTC(date);
    if (!formattedDate || !selectedVilla) return;

    const today = new Date();
    if (date < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
      toast.info('You cannot modify past dates');
      return;
    }

    try {
      const villaRef = doc(db, 'villas', selectedVilla);

      if (isDateBlocked(date)) {
        await updateDoc(villaRef, {
          blockedDates: arrayRemove(formattedDate),
        });
        setBlockedDates((prev) => prev.filter((d) => d !== formattedDate));
        toast.success('Date unblocked successfully');
      } else {
        await updateDoc(villaRef, {
          blockedDates: arrayUnion(formattedDate),
        });
        setBlockedDates((prev) => [...prev, formattedDate]);
        toast.success('Date blocked successfully');
      }

      setSelectedDates([]);
    } catch (err) {
      console.error('Error updating date:', err);
      toast.error('Failed to update date');
    }
  };

  const changeMonth = (delta) => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const renderMonth = (offset) => {
    const today = new Date();
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset);
    const daysInMonth = getDaysInMonth(date);
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(date.getFullYear(), date.getMonth(), i);
      const isDisabled = currentDate < today || isDateBlocked(currentDate);
      const isSelected = isDateSelected(currentDate);
      const isRange = isInRange(currentDate);

      days.push(
        <div
          key={i}
          className={`calendar-day ${isDisabled ? 'disabled' : ''} ${isSelected && !isDisabled ? 'selected' : ''} ${isRange ? 'in-range' : ''}`}
          onClick={() => handleDateClick(currentDate)}
        >
          {i}
        </div>
      );
    }

    return (
      <div className="calendar-month">
        <div className="calendar-header">
          {date.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </div>
        <div className="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}
          {days}
        </div>
      </div>
    );
  };

  if (loading) return <div className="spinner-container"><div className="spinner"></div></div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (villas.length === 0) return <div className="error">No villas available</div>;

  const selectedVillaName = villas.find((v) => v.id === selectedVilla)?.title || 'Selected Villa';

  return (
    <div className="villa-availability-container">
      <h2>Villa Availability</h2>
      <div className="villa-selector">
        <label htmlFor="villaSelect">Select Villa:</label>
        <select
          id="villaSelect"
          value={selectedVilla || ''}
          onChange={handleVillaChange}
          className="custom-selectb"
        >
          {villas.map((villa) => (
            <option key={villa.id} value={villa.id}>
              {villa.title}
            </option>
          ))}
        </select>
      </div>

      <div className="calendar-container-booking">
        <h3 className="villa-name-heading">{selectedVillaName} Availability</h3>
        <div className="calendar-header-controls">
          <div className="calendar-navigation">
            <button className="btn-nav" onClick={() => changeMonth(-2)}>←</button>
            <button className="btn-nav" onClick={() => changeMonth(2)}>→</button>
          </div>
        </div>
        <div className="calendar-content">
          {renderMonth(0)}
          {renderMonth(1)}
        </div>
      </div>

      <div className="blocked-dates">
        <h4>Blocked Dates</h4>
        {blockedDates.length > 0 ? (
          <ul>
            {blockedDates.sort().map((date) => (
              <li key={date}>
                {date}
                <button className="unblock-button" onClick={() => handleDateClick(new Date(date))}>
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p>No dates blocked for this villa.</p>
        )}
      </div>
    </div>
  );
};

export default VillaBookingCalendar;
